import { t } from '../i18n/I18n';
import { allows, nextTier, requiredTier, TIER_NAMES, type ExportFormat, type PaidTier, type Tier } from '../state/demoTier';
import type { MenuItem, MenuRow, SubmenuItem } from './Dropdown';
import type { ContextToolbarModel, ToolbarEntry } from './contextToolbar';
import { store, type EditScope, type Selection } from '../state/store';

/** The app's support address (`Settings.EmailSupport`). */
export const SUPPORT_EMAIL = 'support@homeplannerapp.com';

export type ExportStep = 'run' | 'demo' | 'paywall';
/** Image and Stats export here; the other formats are made by the server (В2), each behind its plan. */
export const exportStep = (tier: Tier, format: ExportFormat): ExportStep =>
  format === 'image' || format === 'stats' ? 'run' : allows(tier, format) ? 'demo' : 'paywall';

const EXPORT_LABELS = { image: 'Image', stats: '#Statistics', pdf: '#WebExportPdf', shoppingList: '#ShoppingList', dwg: 'DWG/DXF', max: '#Benefit3dsMax', crm: '#CrmAndSheets' } as const;
export const exportLabel = (format: ExportFormat): string => t(EXPORT_LABELS[format]);
const UPGRADE_LABELS = { pro: '#WebUpgradeToPro', proPlus: '#WebUpgradeToProPlus', proPlusPlus: '#UpgradeToProPlusPlus' } as const;

/** «Export ›»: what this view exports now first, the server's formats after the line, badged with the plan they need. */
export function exportRows(tier: Tier, available: { image: boolean; stats: boolean }, run: (format: ExportFormat) => void): SubmenuItem[] {
  const row = (format: ExportFormat): SubmenuItem => {
    const needs = requiredTier(format);
    return { id: 'export-' + format, label: exportLabel(format), disabled: format === 'image' || format === 'stats' ? !available[format] : undefined,
      badge: needs === 'free' ? undefined : { label: TIER_NAMES[needs], tone: needs }, onSelect: () => run(format) };
  };
  return [row('image'), row('stats'), row('pdf'), row('shoppingList'), { id: 'export-server', separator: true }, row('dwg'), row('max'), row('crm')];
}

/** Help and Feedback: the «?» button's menu and the same rows in «More». */
export const helpRows = (openShortcuts: () => void): SubmenuItem[] => [
  { id: 'help-shortcuts', label: t('#WebKeyboardShortcuts'), shortcut: 'shortcuts', onSelect: openShortcuts },
  { id: 'help-support', label: t('#LeaveTeamWriteSupport'), href: 'mailto:' + SUPPORT_EMAIL },
];

/** The banner row offering the next plan; the top plan has none. */
export function upgradeRow(tier: Tier, upgrade: (tier: PaidTier) => void): MenuItem | null {
  const next = nextTier(tier);
  return next ? { id: 'upgrade', label: t(UPGRADE_LABELS[next]), className: 'menu-upgrade upgrade-' + next, onSelect: () => upgrade(next) } : null;
}

/** «More» (Figma 990:15121): Save a copy · Export › · — · Help and Feedback › · the Upgrade banner last. */
export function moreRows({ saveCopy, exports, help, developer, upgrade }: {
  saveCopy: (() => void) | null; exports: SubmenuItem[]; help: SubmenuItem[]; upgrade: MenuItem | null;
  /** The service rows of development builds and `?dev`. */
  developer: SubmenuItem[] | null;
}): MenuItem[] {
  return [
    ...(saveCopy ? [{ id: 'save-copy', label: t('#WebSaveCopy'), onSelect: saveCopy }] : []),
    { id: 'export', label: t('Export'), submenu: exports },
    { id: 'help-separator', separator: true },
    { id: 'help', label: t('#HelpAndFeedback'), submenu: help },
    ...(developer?.length ? [{ id: 'developer', label: t('#WebDeveloper'), submenu: developer }] : []),
    ...(upgrade ? [upgrade] : []),
  ];
}

export type CanvasView = 'floor' | 'ceiling' | 'wall' | 'orbit' | 'walk';
/** `scene`: the floor has a 3D to look out of; `gallery`: the header has no Gallery button in this view. */
export interface CanvasMenuState { view: CanvasView; edit: EditScope; canPaste: boolean; levelWalls: boolean; dimensions: boolean; scene: boolean; gallery: boolean }
export type CanvasCommand = 'paste' | 'selectAll' | 'fit' | 'zoomReset' | 'shortcuts' | 'dimensions' | 'measure' | 'planScale' | 'planRotate' | 'lighting' | 'windowView' | 'gallery';

/** The menu of a context press on nothing (Figma 1040:16891), then the rows More › View had, in every view it had them (В1). */
export function canvasMenuRows({ view, edit, canPaste, levelWalls, dimensions, scene: hasScene, gallery }: CanvasMenuState, run: (command: CanvasCommand) => void): MenuItem[] {
  const row = (command: CanvasCommand, label: string, extra: Partial<MenuRow> = {}): MenuItem => ({ id: 'canvas-' + command, label, onSelect: () => run(command), ...extra });
  const editable = edit !== 'none', scene = view === 'orbit' || view === 'walk';
  const tools: MenuItem[] = [
    row('dimensions', dimensions ? t('#EditorHideDimensions') : t('#EditorShowDimensions')),
    ...(levelWalls ? [row('measure', t('Measure'))] : []),
    ...(levelWalls && edit === 'all' ? [row('planScale', t('#ChangeFloorplanScale')), row('planRotate', t('#EditorRotatePlan'))] : []),
    ...(scene ? [row('lighting', t('Lighting'))] : []),
    ...(hasScene ? [row('windowView', t('#EditorWindowView'))] : []),
    ...(gallery ? [row('gallery', t('Gallery'))] : []),
  ];
  return [
    ...(editable ? [row('paste', t('#WebPasteHere'), { shortcut: 'paste', disabled: !canPaste })] : []),
    ...(editable && view !== 'wall' ? [row('selectAll', t('#SelectAll'), { shortcut: 'selectAll' })] : []),
    row('fit', t('#WebZoomToFit'), { shortcut: 'fit' }),
    ...(view !== 'walk' ? [row('zoomReset', t('#WebZoomTo100'), { shortcut: 'zoomReset' })] : []),
    row('shortcuts', t('#WebKeyboardShortcuts'), { shortcut: 'shortcuts' }),
    ...(tools.length ? [{ id: 'canvas-tools', separator: true } as const, ...tools] : []),
  ];
}

/** The selection's menu: its toolbar in order («⋯» spelled out), for furniture Figma 1040:17037's Copy · Paste · Duplicate · Position · Lock, the removal last in red. */
export function objectMenuRows(selection: NonNullable<Selection>, model: ContextToolbarModel,
  clipboard: { canPaste: boolean; copy: () => void; paste: () => void; lock?: { locked: boolean; toggle: () => void } }): MenuItem[] {
  const furniture = selection.kind === 'item' || selection.kind === 'items';
  const entryRow = (entry: ToolbarEntry): MenuItem => ({ id: 'tool-' + entry.slot, label: entry.label, danger: entry.danger, disabled: !!entry.disabled, checked: entry.pressed,
    onSelect: entry.run, submenu: entry.menu?.map(item => ({ id: 'tool-' + entry.slot + '-' + item.id, label: item.label, onSelect: item.run })) });
  const head: MenuItem[] = [], block: MenuItem[] = [];
  let position: MenuItem | null = null, duplicate: MenuItem | null = null, remove: MenuItem | null = null;
  for (const entry of model.entries) {
    if (entry.slot === 'clone') duplicate = { ...entryRow(entry), label: furniture ? t('#WebDuplicate') : entry.label, shortcut: furniture || selection.kind === 'room' ? 'duplicate' : undefined };
    else if (entry.slot === 'remove') remove = { ...entryRow(entry), label: furniture ? t('Delete') : entry.label, danger: true, shortcut: 'delete' };
    else if (entry.slot === 'more' && entry.menu) for (const item of entry.menu) {
      const row: MenuItem = { id: 'more-' + item.id, label: item.label, onSelect: item.run, disabled: !!entry.disabled };
      if (furniture && item.id === 'position') position = row; else if (!(furniture && item.id === 'lock')) head.push(row);
    }
    else head.push(entryRow(entry));
  }
  head.push(...model.pills.filter(pill => pill.id !== 'unlock').map(pill => ({ id: 'pill-' + pill.id, label: pill.label, onSelect: pill.run })));
  if (furniture) block.push({ id: 'copy', label: t('Copy'), shortcut: 'copy', onSelect: clipboard.copy },
    { id: 'paste', label: t('Paste'), shortcut: 'paste', disabled: !clipboard.canPaste, onSelect: clipboard.paste });
  if (duplicate) block.push(duplicate);
  if (position) block.push(position);
  if (furniture && clipboard.lock) block.push({ id: 'lock', label: clipboard.lock.locked ? t('Unlock') : t('Lock'), shortcut: 'lock', onSelect: clipboard.lock.toggle });
  return [head, block, remove ? [remove] : []].filter(group => group.length)
    .flatMap((group, index) => index ? [{ id: 'object-separator-' + index, separator: true } as const, ...group] : group);
}

/** What the object menu acts on: only a selection the press itself decided, and never a viewer's. */
export const contextTarget = (selection: Selection, decided: boolean, edit: EditScope): Selection => decided && edit !== 'none' ? selection : null;

/** A Shift-gathered group has no toolbar of its own: duplicate and remove, as the keys do. */
export const groupToolbar = (): ContextToolbarModel => ({ pills: [], entries: [
  { slot: 'clone', label: t('#WebDuplicate'), run: () => store.duplicateSelection() },
  { slot: 'remove', label: t('Delete'), danger: true, run: () => store.removeSelection() },
] });
